import { useMemo } from "react";
import {
  todayGapSets,
  todayGapsFromFit,
  type ModelFitGaps,
  type TodayGapSets,
} from "@heraion/shared";

import { sunkSlugsAt } from "./muscleCoverageProposal";
import { todayGapsFromCoverage } from "./todayGapsFromCoverage";
import type { ProgressDeps } from "./types";
import { coverageOf, useMuscleCoverage } from "./useMuscleCoverage";
import { useSessionResultsFromDeps } from "./useSessionResults";

// #492 F5, «plan the gaps»: today's draft from three doors. #540: each hands
// the draft both candidate sets (todayGapSets); its toggle picks one.

/** The coverage, and today's draft gaps off it: Progress and the Create hub.
 *  `planToday` is null until the results and the catalog are in. */
export function usePlanGapsCoverage(deps: ProgressDeps) {
  const read = useMuscleCoverage(deps);
  const { coverage, results, catalog } = read;
  const place = deps.trainingPlace;
  const planToday = useMemo(
    () =>
      results && catalog
        ? (): TodayGapSets => {
            const sunk = sunkSlugsAt(place, results);
            return todayGapSets(todayGapsFromCoverage(coverage), catalog, (ex) => !sunk.has(ex.slug));
          }
        : null,
    [coverage, results, catalog, place],
  );
  return { ...read, planToday };
}

/** The program card's fit (modelFitGaps). */
export type PlanGapsLauncher = (input: {
  fit: Pick<ModelFitGaps, "belowMev" | "recommendations">;
}) => void;

/** The Home program card's door: the cycle's gaps still open this week
 *  (todayGapsFromFit), at the athlete's place, read on the tap. */
export function usePlanGapsLauncher(deps: ProgressDeps): PlanGapsLauncher | undefined {
  const { results, catalog } = useSessionResultsFromDeps(deps);
  return useMemo(() => {
    const onBuildToday = deps.onBuildToday;
    if (!onBuildToday || !results || !catalog) return undefined;
    return ({ fit }) => {
      const week = coverageOf(results, catalog, Date.now(), deps)?.byMuscle;
      const sunk = sunkSlugsAt(deps.trainingPlace, results);
      const fromFit = todayGapsFromFit(fit, {
        weekSets: week?.last7DaysSets ?? new Map(),
        ...(week ? { lastTrainedMs: week.lastTrainedMs } : {}),
        ...(sunk.size > 0
          ? { slugs: new Set(catalog.filter((e) => !sunk.has(e.slug)).map((e) => e.slug)) }
          : {}),
      });
      const { gaps, ...bodyweight } = todayGapSets(fromFit, catalog, (ex) => !sunk.has(ex.slug));
      onBuildToday(gaps, catalog, bodyweight);
    };
  }, [deps, results, catalog]);
}
